import { FC, MouseEventHandler } from 'react';
import { graphql, useFragment } from 'react-relay';
import FavoritesItemProvider from 'dibs-portfolio-folder/exports/FavoritesItemProvider';
import { ItemTile } from 'dibs-search-product-tile/exports/ItemTile';
import { FavoritesItem } from '../../components/global/FavoriteIcons/FavoritesItem';
import { VERTICAL_OFFSET_RESP_IMG, SRC_SET_SIZES_TILE } from '../../constants/imageConstants';
import classNames from 'classnames';

import styles from './SharedRespRelatedItem.scss';

import { SharedRespRelatedItem_item$key } from './__generated__/SharedRespRelatedItem_item.graphql';
import { SharedRespRelatedItem_user$key } from './__generated__/SharedRespRelatedItem_user.graphql';
import { SharedRespRelatedItem_viewer$key } from './__generated__/SharedRespRelatedItem_viewer.graphql';
import { QuickViewProviderChildrenProps } from '../../global/QuickViewProvider/QuickViewProvider';
import { FavoritesProviderChildrenProps } from '../../components/global/FavoriteIcons/FavoritesProviderChildrenProps';

const SMALL_TILE_TYPE = 'small';
export const X_SMALL_TILE_TYPE = 'xSmall';

type Props = {
    item: SharedRespRelatedItem_item$key;
    user: SharedRespRelatedItem_user$key | null;
    viewer: SharedRespRelatedItem_viewer$key | null;
    customClassName?: string | null;
    index: number;
    currency: string;
    onClick?: MouseEventHandler;
    quickView: QuickViewProviderChildrenProps;
    favorites: FavoritesProviderChildrenProps;
    lazyLoadStartIndex?: number;
    showLightBox?: boolean;
    showMeasurements?: boolean;
    hideTileImagePadding?: boolean;
    tileType?: typeof X_SMALL_TILE_TYPE | typeof SMALL_TILE_TYPE;
    showAuctionBids?: boolean;
    badgeBgColor?: string;
    showNoReserveBadge?: boolean;
    isCondensedGrid?: boolean;
};

export const SharedRespRelatedItem: FC<Props> = ({
    index,
    customClassName,
    item: itemRef,
    onClick,
    user: userRef,
    viewer: viewerRef,
    quickView,
    favorites,
    currency,
    lazyLoadStartIndex = 0,
    showLightBox = false,
    showMeasurements,
    hideTileImagePadding,
    tileType = SMALL_TILE_TYPE,
    showAuctionBids,
    badgeBgColor,
    showNoReserveBadge,
    isCondensedGrid,
}) => {
    const viewer = useFragment(
        graphql`
            fragment SharedRespRelatedItem_viewer on Viewer {
                ...ItemTile_viewer
            }
        `,
        viewerRef
    );
    const item = useFragment(
        graphql`
            fragment SharedRespRelatedItem_item on Item
            @argumentDefinitions(
                userCountryCode: { type: "String" }
                priceBookName: { type: "String" }
            ) {
                serviceId
                isFurniture
                firstPhotoWebPath(size: thumb)
                ...ItemTile_item
                    @arguments(
                        isTrade: $isTrade
                        userCountryCode: $userCountryCode
                        priceBookName: $priceBookName
                    )
                ...FavoritesItem_item
                ...FavoritesItemProvider_item
            }
        `,
        itemRef
    );
    const user = useFragment(
        graphql`
            fragment SharedRespRelatedItem_user on User {
                canShowSellerName
                ...FavoritesItemProvider_user
            }
        `,
        userRef
    );

    const { activeQuickView, openQuickView, closeQuickView } = quickView || {};
    const { canShowSellerName } = user || {};
    const canShowCreatorName = !canShowSellerName && item.isFurniture;

    const getImageSize = (): string => {
        if (isCondensedGrid) {
            return 'imageXxSmall';
        } else {
            return tileType === X_SMALL_TILE_TYPE ? 'imageXSmall' : 'imageSmall';
        }
    };
    const heartIconWrapperClass = classNames(styles.favoritesIconWrapper, {
        [styles.isCondensedGrid]: isCondensedGrid,
    });
    // Default Layout
    return (
        <div className={classNames(styles.item, styles[tileType], customClassName)}>
            <FavoritesItemProvider item={item} user={user} applyMouseEvents>
                {({ isFolderVisible, ...favoritesItemProps }) => {
                    const favoritesComponent = favorites ? (
                        <div className={classNames(styles.favorites, styles[tileType])}>
                            <FavoritesItem
                                wrapperClass={styles.favoritesWrapper}
                                heartIconWrapperClass={heartIconWrapperClass}
                                folderIconWrapperClass={heartIconWrapperClass}
                                {...favorites}
                                item={item}
                                itemImage={item.firstPhotoWebPath || ''}
                                fetchFolder={favorites.fetchFolder && !!isFolderVisible}
                                {...favoritesItemProps}
                            />
                        </div>
                    ) : null;

                    return (
                        <ItemTile
                            showInfo={!isCondensedGrid}
                            disableContentMargin={tileType === X_SMALL_TILE_TYPE}
                            index={index}
                            item={item}
                            currency={currency}
                            itemsPerRow={lazyLoadStartIndex}
                            showSellerName={canShowSellerName}
                            showCreatorName={canShowCreatorName}
                            onClick={onClick}
                            activeQuickView={activeQuickView}
                            openQuickView={openQuickView}
                            closeQuickView={closeQuickView}
                            showQuickView={!isCondensedGrid && !!openQuickView && !!closeQuickView}
                            showLightBox={showLightBox}
                            alignCenter={showLightBox}
                            disableBorder
                            useLoFiLazyLoader
                            imageSize={getImageSize()}
                            itemSearch={null}
                            imageLoadVerticalOffset={VERTICAL_OFFSET_RESP_IMG}
                            viewer={viewer}
                            srcSetSizes={SRC_SET_SIZES_TILE}
                            showMSKUSwatch={false}
                            favoritesComponent={favoritesComponent}
                            showMeasurements={showMeasurements}
                            hideImagePadding={isCondensedGrid || hideTileImagePadding}
                            showAuctionBids={showAuctionBids}
                            badgeBgColor={badgeBgColor}
                            showNoReserveBadge={showNoReserveBadge}
                        />
                    );
                }}
            </FavoritesItemProvider>
        </div>
    );
};
